import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import { paginate } from '../../../lib/nest';
import { Catalog } from '../../../modules/catalogs/catalog.types';
import { formatFilterIdsSearchParams } from '../../../modules/catalogs/products/catalogFilters.utils';
import { catalogsRepository } from '../../../modules/catalogs/repositories/catalogs.repository';
import { EMPTY_OFFLINE_SECTION_PRODUCT_SET } from '../../../modules/catalogs/repositories/types/catalogs.repository.types';
import { isGetCatalogSectionsOfflineOutput } from '../../../modules/catalogs/repositories/utils/catalogSections.utils';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { PaginatedResponse } from '../../../types/request.types';
import {
  getFindDefaultCatalogKey,
  getFindSuggestedCatalogsKey,
  getGetCatalogKey,
  getGetCatalogSectionsKey,
} from './catalogs.keys';
import {
  CatalogSectionsQueryData,
  GetCatalogQueryParams,
  GetCatalogSectionsQueryParams,
} from './catalogs.queries.types';
import { findCatalogs } from './catalogs.repository';

const SUGGESTED_CATALOGS_STALE_TIME_MS = 60_000;

export const useFindSuggestedCatalogs = (
  limit: number,
  options?: UseDataQueryOptions<PaginatedResponse<Catalog>>,
) => {
  const { brandId } = useApplicationContext();

  return useDataQuery<PaginatedResponse<Catalog>>(
    {
      queryKey: getFindSuggestedCatalogsKey(limit, brandId),
      queryFn: async (context) => {
        const response = await findCatalogs(
          { active: true, isDefault: false, limit, ...paginate.order({ position: 'ASC' }) },
          context?.signal,
        );
        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('failed to fetch suggested catalogs');
        }
        return response.data;
      },
      staleTime: SUGGESTED_CATALOGS_STALE_TIME_MS,
    },
    options,
  );
};

export const useGetCatalog = (
  params: GetCatalogQueryParams,
  options?: UseDataQueryOptions<Catalog>,
) =>
  useDataQuery<Catalog>(
    {
      queryKey: getGetCatalogKey(params.id, params.isOfflineMode),
      queryFn: async () => {
        const response = await catalogsRepository(params.id, params.isOfflineMode).getOne();
        if (!response.ok || !response.data) {
          throw response;
        }
        return response.data as Catalog;
      },
    },
    options,
  );

export const useFindDefaultCatalog = (options?: UseDataQueryOptions<Catalog | null>) =>
  useDataQuery<Catalog | null>(
    {
      queryKey: getFindDefaultCatalogKey(),
      queryFn: async (context) => {
        const response = await findCatalogs({ isDefault: true, limit: 1 }, context?.signal);
        if (!response.ok || !response.data) {
          throw response.originalError ?? new Error('failed to fetch the default catalog');
        }
        return response.data.data[0] ?? null;
      },
    },
    options,
  );

export const useGetCatalogSections = (
  params: GetCatalogSectionsQueryParams,
  options?: UseDataQueryOptions<CatalogSectionsQueryData>,
) =>
  useDataQuery<CatalogSectionsQueryData>(
    {
      queryKey: getGetCatalogSectionsKey(params),
      queryFn: async () => {
        const formattedFilters = formatFilterIdsSearchParams(params.filters, params.customerIds);

        const output = await catalogsRepository(params.id, params.isOfflineMode).getCatalogSections(
          {
            ...params.contextParams,
            ...params.groupParams,
            ...formattedFilters,
          },
        );
        const { sectionsResponse } = output;

        if (!sectionsResponse.ok || !sectionsResponse.data) {
          throw sectionsResponse.originalError ?? new Error('Error fetching catalog sections');
        }

        const offlineSectionProductSet = isGetCatalogSectionsOfflineOutput(output)
          ? output.offlineSectionProductSet
          : EMPTY_OFFLINE_SECTION_PRODUCT_SET;

        return { sections: sectionsResponse.data, offlineSectionProductSet };
      },
    },
    options,
  );
